import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { CheckmarkFilled, CloseFilled } from "@carbon/icons-react";
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  TextInput,
  Toggle,
} from "@carbon/react";
import { FileReader, SampleItem } from "..";
import { IT_DOESNT_HAVE_SPEC } from "../../utils";
import {
  CertificationResponse,
  CombinedBuyerSellerMaterialOfferResponse,
  SampleEnum,
} from "../../types";
import "./materialOffer.scss";

const combineCertifications = (
  materials: CertificationResponse[],
  supplierCerts: any
) => {
  return materials?.map((material) => {
    const supplierCert = supplierCerts?.find(
      (cert: any) => cert.materialCertification.id === material.id
    );
    return {
      ...material,
      hasSupplierCert: !!supplierCert,
    };
  });
};

type MaterialOfferProps = {
  open: boolean;
  materialOffer: CombinedBuyerSellerMaterialOfferResponse;
  onValidateSeller: (id: string) => void;
  onChangeToggle: () => void;
  onRejectSeller: ({
    buyerSellerId,
    buyerSeller,
  }: {
    buyerSellerId: string;
    buyerSeller: any;
  }) => void;
  setOpen: () => void;
  onApproveSample: ({
    materialOfferId,
    body,
  }: {
    materialOfferId: string;
    body: any;
  }) => void;
  onRequestSample: (id: string) => void;
  onRejectSample: ({
    materialOfferId,
    body,
  }: {
    materialOfferId: string;
    body: any;
  }) => void;
};

/**
 * The supplier "application" (postulación) detail — rebuilt on Carbon as a self-contained
 * `ComposedModal` (it opens directly from `Material.tsx`, replacing the pre-Carbon headless-ui
 * `<Slide>` + Tailwind card). The provider info is a Carbon `StructuredList`, the validate control a
 * Carbon `Toggle`, and the request-sample / reject actions Carbon `Button`s; their two confirmation
 * sub-dialogs are their own `ComposedModal`s. Every mutation (validate / reject-with-comment /
 * request-, approve-, reject-sample) is preserved exactly.
 */
const MaterialOffer = ({
  open,
  setOpen,
  materialOffer,
  onRejectSeller,
  onChangeToggle,
  onRejectSample,
  onRequestSample,
  onApproveSample,
  onValidateSeller,
}: MaterialOfferProps) => {
  const { t } = useTranslation();
  const [openReject, setOpenReject] = useState(false);
  const [openSample, setOpenSample] = useState(false);
  const [rejectMessage, setRejectMessage] = useState("");

  const userCompanyType = new URLSearchParams(useLocation().search).get(
    "userType"
  );

  const offer = materialOffer?.materialOffer;
  const buyerSeller = materialOffer?.buyerSeller;
  const companyName = offer?.user?.company?.name;
  const sellerCompanyName = buyerSeller?.seller?.company?.name;
  const validated = buyerSeller?.validated;

  const handleOfferConfirmation = () => {
    if (!rejectMessage) return;
    onRejectSeller({
      buyerSellerId: buyerSeller?.id,
      buyerSeller: {
        ...buyerSeller,
        comment: rejectMessage,
      },
    });
    setOpenReject(false);
    setOpen();
  };

  const handleSampleRequest = () => {
    onRequestSample(offer.id);
    setOpenSample(false);
  };

  const handleApproveSample = () => {
    onApproveSample({
      materialOfferId: offer.id,
      body: { ...offer, state: "VERIFIED" },
    });
  };

  const handleRejectSample = () => {
    onRejectSample({
      materialOfferId: offer.id,
      body: { ...offer, state: "REJECTED" },
    });
  };

  const combinedData = combineCertifications(
    offer?.material?.certifications,
    offer?.certifications
  );

  const showFooter = !validated && !userCompanyType;
  const canRequestSample = offer?.state === SampleEnum.INITIALIZED;

  const infoRow = (label: string, value?: string) => (
    <StructuredListRow>
      <StructuredListCell head>{label}</StructuredListCell>
      <StructuredListCell>{value || "-"}</StructuredListCell>
    </StructuredListRow>
  );

  return (
    <Fragment>
      <ComposedModal open={open} onClose={setOpen} size="md">
        <ModalHeader label={t("offer.title")} title={companyName} />
        <ModalBody>
          <StructuredListWrapper
            className="material-offer__info"
            isCondensed
            ariaLabel={t("offer.information")}
          >
            <StructuredListBody>
              {infoRow(t("offer.taxId"), offer?.user?.company?.taxId)}
              {infoRow(t("offer.seller"), offer?.user?.fullName)}
              {infoRow(t("offer.email"), offer?.user?.email)}
              {infoRow(t("offer.phone"), offer?.user?.phone)}
              {infoRow(t("offer.origin"), offer?.origin?.name)}
              {offer?.comment
                ? infoRow(t("offer.comments"), offer.comment)
                : null}
            </StructuredListBody>
          </StructuredListWrapper>

          {offer?.material?.certifications?.length > 0 && (
            <section className="material-offer__block">
              <h3 className="material-offer__block-title">
                {t("certifications.certifications")}
              </h3>
              <ul className="material-offer__certs">
                {combinedData?.map((cert) => (
                  <li key={cert.id} className="material-offer__cert">
                    {cert.hasSupplierCert ? (
                      <CheckmarkFilled
                        size={16}
                        className="material-offer__cert-ok"
                      />
                    ) : (
                      <CloseFilled
                        size={16}
                        className="material-offer__cert-missing"
                      />
                    )}
                    <span>
                      {cert.name}
                      {cert.required && (
                        <span className="material-offer__required"> *</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="material-offer__block">
            <h3 className="material-offer__block-title">
              {t("newMaterialForm.specification")}
            </h3>
            {offer?.specificationFileUrl ? (
              <FileReader fileKey={offer.specificationFileUrl} />
            ) : (
              <p className="material-offer__muted">{IT_DOESNT_HAVE_SPEC}</p>
            )}
          </section>

          {!userCompanyType && (
            <div className="material-offer__validate">
              <Toggle
                id="material-offer-validate"
                size="sm"
                toggled={!!validated}
                labelText={t("offer.validateSupplier")}
                labelA={t("offer.validateSupplier")}
                labelB={t("offer.validated")}
                onToggle={() => {
                  onChangeToggle();
                  onValidateSeller(buyerSeller?.id);
                }}
              />
            </div>
          )}

          <SampleItem
            userCompanyType={userCompanyType}
            stateSample={offer.state}
            handleApproveSample={handleApproveSample}
            handleRejectSample={handleRejectSample}
          />
        </ModalBody>
        {showFooter && (
          <ModalFooter>
            {canRequestSample && (
              <Button kind="secondary" onClick={() => setOpenSample(true)}>
                {t("offer.requestSample")}
              </Button>
            )}
            <Button kind="danger--tertiary" onClick={() => setOpenReject(true)}>
              {t("offer.reject")}
            </Button>
          </ModalFooter>
        )}
      </ComposedModal>

      {/* Confirmation sub-dialogs render AFTER the main modal so they stack on top of it (Carbon
          gives every modal the same z-index, so DOM order decides which is in front). */}
      <ComposedModal
        open={openReject}
        onClose={() => setOpenReject(false)}
        size="sm"
      >
        <ModalHeader title={`${t("offer.reject")}: ${sellerCompanyName}`} />
        <ModalBody hasForm>
          <p className="material-offer__reject-question">
            {t("offer.question")}
          </p>
          <TextInput
            id="material-offer-reject-comment"
            labelText={t("tenders.data.cancelBrief")}
            value={rejectMessage}
            onChange={(e) => setRejectMessage(e.target.value)}
            required
          />
        </ModalBody>
        <ModalFooter>
          <Button kind="secondary" onClick={() => setOpenReject(false)}>
            {t("cta.cancel")}
          </Button>
          <Button
            kind="danger"
            disabled={!rejectMessage}
            onClick={handleOfferConfirmation}
          >
            {t("cta.reject")}
          </Button>
        </ModalFooter>
      </ComposedModal>

      <ComposedModal
        open={openSample}
        onClose={() => setOpenSample(false)}
        size="sm"
      >
        <ModalHeader title={`${t("offer.requestFrom")} ${sellerCompanyName}`} />
        <ModalBody>
          <p>{t("offer.disclaimer")}</p>
        </ModalBody>
        <ModalFooter>
          <Button kind="secondary" onClick={() => setOpenSample(false)}>
            {t("cta.cancel")}
          </Button>
          <Button onClick={handleSampleRequest}>{t("cta.request")}</Button>
        </ModalFooter>
      </ComposedModal>
    </Fragment>
  );
};

export default MaterialOffer;
